import { memo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircleOutline as CheckIcon, WarningAmberOutlined as WarnIcon } from '@mui/icons-material';
import VersionDiff from './VersionDiff';
import { useDeploymentParameters } from '../hooks/useDeploymentParameters';
import type { Deployment } from '../../../types/backend';
import type { DerivedState } from '../rowConfig';
import type { DeploymentRowActions } from '../hooks/useDeploymentRowActions';

interface DeploymentRowOverviewProps {
  customerId: string;
  deployment: Deployment;
  state: DerivedState;
  onSwitchToParameters: () => void;
  actions: DeploymentRowActions;
  recentVersions: string[];
}

const MetaGrid: React.FC<{ rows: Array<{ label: string; value: React.ReactNode; mono?: boolean }> }> = ({ rows }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
    {rows.map((r, i) => (
      <Box key={i}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {r.label}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: r.mono ? 'monospace' : undefined }}>
          {r.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

interface PendingApprovalOverviewProps {
  customerId: string;
  deployment: Deployment;
  actions: DeploymentRowActions;
}

const PendingApprovalOverview: React.FC<PendingApprovalOverviewProps> = ({ customerId, deployment, actions }) => {
  const { preview, previewLoading, previewUnavailable } = useDeploymentParameters({
    customerId,
    deployment,
    onSuccess: () => undefined,
  });

  const stackName =
    deployment.actual_stack_id?.split(':').pop()?.split('/')[1] ?? '—';

  const unresolved = preview?.unresolved ?? [];
  const paramChip = previewLoading ? null : previewUnavailable || !preview ? (
    <Chip icon={<WarnIcon />} label="Preview unavailable" color="warning" variant="outlined" size="small" />
  ) : unresolved.length > 0 ? (
    <Tooltip title={unresolved.join(', ')} placement="top">
      <Chip
        icon={<WarnIcon />}
        label={`${unresolved.length} parameter(s) unresolved`}
        color="warning"
        variant="outlined"
        size="small"
      />
    </Tooltip>
  ) : (
    <Chip icon={<CheckIcon />} label="All parameters resolved" color="success" variant="outlined" size="small" />
  );

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {paramChip}
      </Box>
      <MetaGrid
        rows={[
          { label: 'Stack', value: stackName, mono: true },
          { label: 'Account', value: deployment.account_id, mono: true },
          { label: 'Region', value: deployment.region },
          { label: 'Desired version', value: deployment.desired_version ?? '—', mono: true },
        ]}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button size="small" onClick={actions.acknowledge} disabled={actions.submitting}>
          Acknowledge
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={actions.approve}
          disabled={actions.submitting}
          startIcon={actions.submitting ? <CircularProgress size={14} /> : <CheckIcon />}
        >
          Approve launch
        </Button>
      </Box>
    </Box>
  );
};

const DeploymentRowOverview = memo<DeploymentRowOverviewProps>(({
  customerId,
  deployment,
  state,
  onSwitchToParameters,
  actions,
  recentVersions,
}) => {
  const [versionInput, setVersionInput] = useState(deployment.desired_version ?? '');

  const stackName =
    deployment.actual_stack_id?.split(':').pop()?.split('/')[1] ?? '—';

  switch (state) {
    case 'missing-params':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Actual version', value: deployment.actual_version ?? '— never deployed' },
              { label: 'Last resolution', value: deployment.drift_reason_detail ?? 'failed' },
              { label: 'Parameters', value: 'see Parameters tab' },
            ]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="contained" onClick={onSwitchToParameters}>
              Fix parameters →
            </Button>
          </Box>
        </Box>
      );

    case 'pending-approval':
      return <PendingApprovalOverview customerId={customerId} deployment={deployment} actions={actions} />;

    case 'drifted':
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Version drift</Typography>
          <VersionDiff
            desired={deployment.desired_version ?? '—'}
            actual={deployment.actual_version ?? '—'}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2 }}>
            <Button size="small" onClick={actions.acknowledge} disabled={actions.submitting}>
              Acknowledge
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={actions.acceptActual} disabled={actions.submitting}>
                Accept actual ({deployment.actual_version})
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={actions.submitting}
                onClick={() => actions.pushDesired({ desired_version: deployment.desired_version })}
              >
                Push desired ({deployment.desired_version})
              </Button>
            </Box>
          </Box>
        </Box>
      );

    case 'rescue-required':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Rescue required since', value: deployment.rescue_required_since ? new Date(deployment.rescue_required_since).toLocaleString() : '—' },
              { label: 'Last action', value: `${deployment.last_action ?? '—'} · ${deployment.last_action_result ?? '—'}` },
              { label: 'Error', value: deployment.last_action_error ?? deployment.rescue_annotation ?? '—' },
            ]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button size="small" onClick={actions.acknowledge} disabled={actions.submitting}>
              Acknowledge
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={actions.resolveRescue}
              disabled={actions.submitting}
              title="Clears rescue_required. The reconciler picks up on its next tick — typically within ~1 min."
            >
              Mark resolved
            </Button>
          </Box>
        </Box>
      );

    case 'in-sync':
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Set desired version</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={versionInput}
              onChange={(e) => setVersionInput(e.target.value)}
              slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              disabled={actions.submitting}
            />
            <Button
              variant="contained"
              size="small"
              disabled={actions.submitting || !versionInput.trim() || versionInput === deployment.desired_version}
              onClick={() => actions.pushDesired({ desired_version: versionInput.trim() })}
            >
              Push
            </Button>
          </Box>
          {recentVersions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, mr: 0.5 }}>
                Recent
              </Typography>
              {recentVersions
                .filter((v) => v !== deployment.desired_version)
                .slice(0, 5)
                .map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    variant="outlined"
                    onClick={() => setVersionInput(v)}
                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                ))}
            </Box>
          )}
        </Box>
      );

    case 'awaiting-heartbeat':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Desired version', value: deployment.desired_version ?? '—', mono: true },
              { label: 'Actual version', value: deployment.actual_version ?? 'unknown', mono: true },
              { label: 'Last heartbeat', value: deployment.last_heartbeat ? new Date(deployment.last_heartbeat).toLocaleString() : 'none' },
              { label: 'Stack', value: stackName, mono: true },
            ]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="contained"
              onClick={actions.requestHeartbeat}
              disabled={actions.submitting}
            >
              Request heartbeat
            </Button>
          </Box>
        </Box>
      );

    case 'awaiting-bootstrap':
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This is a bootstrap product. It will be provisioned when the customer account is onboarded in this region.
          </Typography>
          <MetaGrid
            rows={[
              { label: 'Desired version', value: deployment.desired_version ?? '—', mono: true },
              { label: 'Actual version', value: deployment.actual_version ?? 'not provisioned', mono: true },
              { label: 'Last heartbeat', value: deployment.last_heartbeat ? new Date(deployment.last_heartbeat).toLocaleString() : 'none' },
              { label: 'Stack', value: stackName, mono: true },
            ]}
          />
        </Box>
      );

    case 'terminating':
      return (
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'info.50', p: 1.25, borderRadius: 1, mb: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="info.main">
              Waiting for teardown to complete — reconciler will mark terminated on next heartbeat.
            </Typography>
          </Box>
          <MetaGrid
            rows={[
              { label: 'Desired status', value: deployment.desired_status },
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Last heartbeat', value: deployment.last_heartbeat ? new Date(deployment.last_heartbeat).toLocaleString() : '—' },
              { label: 'Started', value: deployment.last_action_at ? new Date(deployment.last_action_at).toLocaleString() : '—' },
            ]}
          />
        </Box>
      );

    case 'deregistering':
      return (
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'info.50', p: 1.25, borderRadius: 1, mb: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="info.main">
              Deregistering — the reconciler is deleting the desired-state parameter. The product keeps running throughout.
            </Typography>
          </Box>
          <MetaGrid
            rows={[
              { label: 'Desired status', value: 'unsubscribed' },
              { label: 'Last actual version', value: deployment.actual_version ?? '—', mono: true },
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Started', value: deployment.last_action_at ? new Date(deployment.last_action_at).toLocaleString() : '—' },
            ]}
          />
        </Box>
      );

    case 'unsubscribed':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Desired status', value: 'unsubscribed' },
              { label: 'Last actual version', value: deployment.actual_version ?? '—', mono: true },
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Updated', value: new Date(deployment.updated_at).toLocaleString() },
            ]}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            OpsPilot no longer manages this deployment — the product is still running in the customer's account. Not destroyed.
          </Typography>
        </Box>
      );

    case 'acknowledged-drift':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Desired', value: deployment.desired_version ?? '—', mono: true },
              { label: 'Actual', value: deployment.actual_version ?? '—', mono: true },
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Acknowledged', value: new Date(deployment.updated_at).toLocaleString() },
            ]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={actions.acceptActual} disabled={actions.submitting}>
                Accept actual
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => actions.pushDesired({ desired_version: deployment.desired_version })}
                disabled={actions.submitting}
              >
                Push desired
              </Button>
            </Box>
          </Box>
        </Box>
      );

    case 'initial-subscription':
    case 'reconciling':
      return (
        <Box sx={{ py: 1 }}>
          <MetaGrid
            rows={[
              { label: 'Desired version', value: deployment.desired_version ?? '—', mono: true },
              { label: 'Actual version', value: deployment.actual_version ?? 'not deployed yet', mono: true },
              { label: 'Stack', value: stackName, mono: true },
              { label: 'Last action', value: deployment.last_action ?? 'none' },
            ]}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Reconciler runs on an interval — no primary action required. Use the kebab to trigger a manual run.
          </Typography>
        </Box>
      );

    case 'unknown':
    default:
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This deployment is in an unrecognized state. See History and drift detail below.
          </Typography>
          <MetaGrid
            rows={[
              { label: 'drift_status', value: deployment.drift_status, mono: true },
              { label: 'drift_reason', value: deployment.drift_reason ?? '—', mono: true },
              { label: 'drift_reason_detail', value: deployment.drift_reason_detail ?? '—' },
              { label: 'blocked', value: deployment.blocked ? 'yes' : 'no' },
            ]}
          />
        </Box>
      );
  }
});

DeploymentRowOverview.displayName = 'DeploymentRowOverview';
export default DeploymentRowOverview;
